import axios from 'axios';
import config from '../utils/config';

// URL base de la API
const API_URL = config.API_URL;

// URL base para autenticación
const AUTH_URL = config.AUTH_URL;

// Instancia de axios para las peticiones
const axiosInstance = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Interceptor para añadir el token a las peticiones
axiosInstance.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Interceptor para manejar errores de token expirado
axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    
    // Si el error es 401 (Unauthorized) y no es un reintento
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      
      try {
        // Intentar refrescar el token
        const refreshToken = localStorage.getItem('refresh_token');
        if (!refreshToken) {
          // Si no hay refresh token, logout
          authService.logout();
          return Promise.reject(error);
        }
        
        // Usar la URL completa para el endpoint de refresh token
        const response = await axios.post(`${AUTH_URL}/api/token/refresh/`, {
          refresh: refreshToken,
        });
        
        // Guardar el nuevo token
        localStorage.setItem('access_token', response.data.access);
        
        // Actualizar el header y reintentar la petición
        originalRequest.headers.Authorization = `Bearer ${response.data.access}`;
        return axiosInstance(originalRequest);
      } catch (refreshError) {
        // Si falla el refresh, logout
        authService.logout();
        return Promise.reject(refreshError);
      }
    }
    
    return Promise.reject(error);
  }
);

// Servicio de autenticación
const authService = {
  // Login
  async login(username, password) {
    try {
      // Usar la URL completa para el endpoint de token
      const response = await axios.post(`${AUTH_URL}/api/token/`, {
        username,
        password,
      });
      
      localStorage.setItem('access_token', response.data.access);
      localStorage.setItem('refresh_token', response.data.refresh);
      
      // Obtener información del usuario
      await this.fetchUserInfo();
      
      return response.data;
    } catch (error) {
      console.error('Error en login:', error);
      throw error;
    }
  },
  
  // Obtener información del usuario
  async fetchUserInfo() {
    try {
      const token = localStorage.getItem('access_token');
      if (!token) return null;
      
      // Configurar el token para esta petición
      const config = {
        headers: {
          Authorization: `Bearer ${token}`
        }
      };
      
      // Obtener información del usuario
      console.log('Obteniendo información del usuario...');
      const response = await axios.get(`${API_URL}/users/me/`, config);
      console.log('Respuesta del servidor (users/me):', response.data);
      
      // Guardar información del usuario en localStorage
      localStorage.setItem('user_info', JSON.stringify(response.data));
      
      return response.data;
    } catch (error) {
      console.error('Error al obtener información del usuario:', error);
      return null;
    }
  },
  
  // Obtener información del usuario desde localStorage
  getUserInfo() {
    const userInfo = localStorage.getItem('user_info');
    const parsedUserInfo = userInfo ? JSON.parse(userInfo) : null;
    console.log('Información del usuario desde localStorage:', parsedUserInfo);
    return parsedUserInfo;
  },
  
  // Verificar si el usuario tiene un rol específico
  hasRole(roleName) {
    const userInfo = this.getUserInfo();
    if (!userInfo || !userInfo.roles) {
      console.log(`hasRole(${roleName}): No hay usuario o roles definidos`, userInfo);
      return false;
    }
    
    // Comprobar tanto role.name como role.role_name para mayor compatibilidad
    const hasTheRole = userInfo.roles.some(role => {
      console.log(`Comparando rol: ${JSON.stringify(role)} con ${roleName}`);
      return role.name === roleName || role.role_name === roleName;
    });
    console.log(`hasRole(${roleName}) = ${hasTheRole}`);
    return hasTheRole;
  },
  
  // Logout
  logout() {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user_info');
  },
  
  // Verificar si está autenticado
  isAuthenticated() {
    return !!localStorage.getItem('access_token');
  },
  
  // Verificar si el token es válido
  async verifyToken() {
    try {
      const token = localStorage.getItem('access_token');
      if (!token) return false;
      
      // Usar la URL completa para el endpoint de verificación de token
      await axios.post(`${AUTH_URL}/api/token/verify/`, {
        token,
      });
      
      return true;
    } catch (error) {
      return false;
    }
  },
  
  // Obtener el token
  getToken() {
    return localStorage.getItem('access_token');
  },
  
  // Obtener la instancia de axios configurada
  getAxiosInstance() {
    return axiosInstance;
  },
};

export default authService; 